import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js';

import {
  deriveSitesFromTreemap,
  filterSitesByType,
  buildHospitalTooltipData,
  noLabelsPlugin,
} from './utils';

const getBinMidpoint = (bin) => {
  const rangeStart = parseInt(bin, 10);
  if (Number.isNaN(rangeStart)) {
    return 0;
  }
  if (bin.includes('+')) {
    return (rangeStart + 100) / 2;
  }
  const parts = bin.split('-');
  const rangeEnd = parts.length > 1 ? parseInt(parts[1], 10) : rangeStart;
  return (rangeStart + rangeEnd) / 2;
};

const getBarColor = (midpoint) => {
  if (midpoint <= 5) return 'rgba(0, 100, 0, 0.7)';
  if (midpoint <= 15) return 'rgba(34, 139, 34, 0.6)';
  if (midpoint <= 25) return 'rgba(144, 238, 144, 0.5)';
  if (midpoint <= 30) return 'rgba(220, 220, 220, 0.5)';
  if (midpoint <= 35) return 'rgba(255, 160, 122, 0.5)';
  if (midpoint <= 45) return 'rgba(178, 34, 34, 0.6)';
  return 'rgba(139, 0, 0, 0.7)';
};

const getBorderColor = (midpoint) => {
  if (midpoint <= 5) return 'rgba(0, 100, 0, 1)';
  if (midpoint <= 15) return 'rgba(34, 139, 34, 0.9)';
  if (midpoint <= 25) return 'rgba(144, 238, 144, 0.8)';
  if (midpoint <= 30) return 'rgba(169, 169, 169, 0.8)';
  if (midpoint <= 35) return 'rgba(255, 160, 122, 0.8)';
  if (midpoint <= 45) return 'rgba(178, 34, 34, 0.9)';
  return 'rgba(139, 0, 0, 1)';
};

const HospitalDistributionSection = ({
  hospitalDistributionData,
  priorityUnmetNeed,
  comparisonAvgLabel,
  comparisonAvgKey,
  cciAdjusted,
  selectedSystem,
  siteTreemapData,
  selectedSiteType,
}) => {
  const siteNodes = useMemo(
    () => deriveSitesFromTreemap(siteTreemapData, selectedSystem.id),
    [siteTreemapData, selectedSystem.id]
  );

  const filteredSites = useMemo(
    () => filterSitesByType(siteNodes, selectedSiteType),
    [siteNodes, selectedSiteType]
  );

  const tooltipData = useMemo(
    () => buildHospitalTooltipData(filteredSites, hospitalDistributionData),
    [filteredSites, hospitalDistributionData]
  );

  const bins = useMemo(() => hospitalDistributionData?.bins ?? [], [hospitalDistributionData]);
  const counts = useMemo(() => hospitalDistributionData?.counts ?? {}, [hospitalDistributionData]);
  const averages = useMemo(() => hospitalDistributionData?.averages ?? {}, [hospitalDistributionData]);

  const maxCount = bins.reduce((max, bin) => {
    const value = counts[bin] || 0;
    return value > max ? value : max;
  }, 0);

  const chartData = useMemo(
    () => ({
      labels: bins,
      datasets: [
        {
          label: 'Number of Sites',
          data: bins.map((bin) => counts[bin] || 0),
          backgroundColor: bins.map((bin) => getBarColor(getBinMidpoint(bin))),
          borderColor: bins.map((bin) => getBorderColor(getBinMidpoint(bin))),
          borderWidth: 1,
        },
        {
          label: 'System Average',
          data: bins.map((bin) => {
            const rangeStart = parseInt(bin, 10);
            const rangeEnd = bin.includes('+') ? 100 : parseInt(bin.split('-')[1], 10);
            const systemAverage = averages.system ?? 0;
            if (Number.isNaN(rangeStart) || Number.isNaN(rangeEnd)) {
              return null;
            }
            return systemAverage >= rangeStart && systemAverage < rangeEnd
              ? maxCount * 0.8
              : null;
          }),
          backgroundColor:
            averages.system > (averages[comparisonAvgKey] ?? 0)
              ? 'rgba(255, 99, 132, 0.8)'
              : 'rgba(75, 192, 192, 0.8)',
          borderColor:
            averages.system > (averages[comparisonAvgKey] ?? 0)
              ? 'rgba(255, 99, 132, 1)'
              : 'rgba(75, 192, 192, 1)',
          borderWidth: 2,
          type: 'line',
          pointStyle: 'triangle',
          pointRadius: 18,
          pointHoverRadius: 22,
          showLine: false,
          datalabels: { display: false },
          display: true,
        },
        {
          label: `${comparisonAvgLabel} Average`,
          data: bins.map((bin) => {
            const rangeStart = parseInt(bin, 10);
            const rangeEnd = bin.includes('+') ? 100 : parseInt(bin.split('-')[1], 10);
            const comparisonAverage = averages[comparisonAvgKey] ?? 0;
            if (Number.isNaN(rangeStart) || Number.isNaN(rangeEnd)) {
              return null;
            }
            return comparisonAverage >= rangeStart && comparisonAverage < rangeEnd ? maxCount * 0.7 : null;
          }),
          backgroundColor: 'rgba(255, 152, 0, 0.8)',
          borderColor: 'rgba(255, 152, 0, 1)',
          borderWidth: 2,
          type: 'line',
          pointStyle: 'circle',
          pointRadius: 15,
          pointHoverRadius: 18,
          showLine: false,
          datalabels: { display: false },
          display: true,
        },
      ],
    }),
    [averages, bins, comparisonAvgKey, comparisonAvgLabel, counts, maxCount]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            generateLabels: (chart) => {
              const originalLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
              const filteredLabels = originalLabels.filter((label) => !label.text.includes('Number of'));
              filteredLabels.forEach((label) => {
                const dataset = chart.data.datasets[label.datasetIndex];
                if (dataset.label === 'System Average') {
                  label.pointStyle = 'triangle';
                  label.rotation = 0;
                } else if (dataset.label.includes('Average')) {
                  label.pointStyle = 'circle';
                }
              });
              return filteredLabels;
            },
          },
        },
        title: {
          display: true,
          text: `Local Distribution of Sites by % of Patients with ${priorityUnmetNeed}${
            cciAdjusted ? ' (CCI Adjusted)' : ''
          }`,
          font: { size: 16 },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              if (label === 'Number of Sites') {
                return `${label}: ${value}`;
              }
              if (label === 'System Average') {
                const comparisonVal = averages[comparisonAvgKey] ?? 0;
                const comparison = (averages.system ?? 0) > comparisonVal ? 'higher than' : 'lower than';
                return `${label}: ${(averages.system ?? 0).toFixed(1)}% (${comparison} ${comparisonAvgLabel} average)`;
              }
              return `${label}: ${(averages[comparisonAvgKey] ?? 0).toFixed(1)}%`;
            },
            afterBody: (context) => {
              if (context.length > 0) {
                const dataPointIndex = context[0].dataIndex;
                const binLabel = bins[dataPointIndex];
                const sitesInBin = tooltipData[binLabel];
                if (sitesInBin && sitesInBin.length > 0) {
                  const tooltipLines = [''];
                  tooltipLines.push('Top Sites in this bucket:');
                  sitesInBin.forEach((site) => {
                    tooltipLines.push(
                      `  ${site.name}: ${site.patientCountForNeed} Pts (${site.unmetNeedPercentage.toFixed(1)}%)`
                    );
                  });
                  return tooltipLines;
                }
              }
              return [];
            },
          },
        },
        averageLabels: noLabelsPlugin,
      },
      scales: {
        y: { beginAtZero: true, title: { display: true, text: 'Number of Sites' } },
        x: { title: { display: true, text: '% of Patients with Priority Unmet Need' } },
      },
    }),
    [averages, bins, cciAdjusted, comparisonAvgKey, comparisonAvgLabel, priorityUnmetNeed, tooltipData]
  );

  return (
    <div className="chart-container">
      <div className="chart-description">
        <p>
          This chart shows the distribution of sites by the percentage of patients with {priorityUnmetNeed}. The{' '}
          <strong>System Average</strong> is shown as a {averages.system > (averages[comparisonAvgKey] ?? 0) ? 'red' : 'green'}
          {' '}triangle (<strong>{(averages.system ?? 0).toFixed(1)}%</strong>,{' '}
          <strong>
            {averages.system > (averages[comparisonAvgKey] ?? 0) ? 'higher/worse' : 'lower/better'} than the {comparisonAvgLabel}{' '}
            average
          </strong>
          ). The <strong>{comparisonAvgLabel} Average</strong> is shown as an orange circle (
          <strong>{(averages[comparisonAvgKey] ?? 0).toFixed(1)}%</strong>).{' '}
          {cciAdjusted && <span className="ml-1 text-blue-600 font-medium">(CCI Adjusted - Not yet implemented)</span>}
        </p>
      </div>
      <Bar data={chartData} options={chartOptions} />
    </div>
  );
};

HospitalDistributionSection.propTypes = {
  hospitalDistributionData: PropTypes.shape({
    bins: PropTypes.array,
    counts: PropTypes.object,
    averages: PropTypes.object,
  }),
  priorityUnmetNeed: PropTypes.string.isRequired,
  comparisonAvgLabel: PropTypes.string.isRequired,
  comparisonAvgKey: PropTypes.string.isRequired,
  cciAdjusted: PropTypes.bool.isRequired,
  selectedSystem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  siteTreemapData: PropTypes.array,
  selectedSiteType: PropTypes.string.isRequired,
};

HospitalDistributionSection.defaultProps = {
  hospitalDistributionData: null,
  siteTreemapData: [],
};

export default HospitalDistributionSection;
